<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { fetchSubscriptionOverview } from '../api';
import { formatBillingDateTime } from '../helpers/formatBillingTime';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();
const currentAccount = useMapGetter('getCurrentAccount');

const loading = ref(false);
const planCode = ref('');
const endsAt = ref('');

const accountId = computed(
  () =>
    store.getters.getCurrentAccountId || store.state.route?.params?.accountId
);

const applyFallbackFromAccount = () => {
  const attrs = currentAccount.value?.custom_attributes || {};
  planCode.value = String(attrs.plan_name || 'free').toLowerCase();
  endsAt.value = attrs.subscription_ends_on || '';
};

const load = async () => {
  if (!accountId.value) {
    applyFallbackFromAccount();
    return;
  }
  loading.value = true;
  try {
    const data = await fetchSubscriptionOverview(accountId.value);
    planCode.value = String(
      data?.subscription?.plan_code || 'free'
    ).toLowerCase();
    endsAt.value = data?.subscription?.current_period_ends_at || '';
  } catch {
    applyFallbackFromAccount();
  } finally {
    loading.value = false;
  }
};

const isPro = computed(() => planCode.value === 'pro');

const label = computed(() => {
  if (loading.value && !planCode.value) {
    return t('CUSTOM.BILLING.SIDEBAR_CARD.LOADING');
  }
  if (isPro.value) {
    const when = formatBillingDateTime(endsAt.value, 'MMM d, yyyy · h:mm a');
    return when
      ? t('CUSTOM.BILLING.SIDEBAR_CARD.PRO_WITH_EXPIRY', { when })
      : t('CUSTOM.BILLING.SIDEBAR_CARD.PRO');
  }
  return t('CUSTOM.BILLING.SIDEBAR_CARD.FREE_UPGRADE');
});

const to = computed(() => accountScopedRoute('custom_subscription'));

watch(accountId, () => load());

onMounted(load);
</script>

<template>
  <div v-if="!isCollapsed" class="w-full px-2 pb-1">
    <RouterLink
      :to="to"
      class="group flex items-center gap-2 w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-safe:transition-colors motion-safe:duration-150 hover:bg-n-alpha-2"
      :aria-label="label"
    >
      <span
        class="flex-shrink-0 size-1.5 rounded-full"
        :class="isPro ? 'bg-n-brand' : 'bg-n-slate-9'"
        aria-hidden="true"
      />
      <span class="min-w-0 flex-1 text-xs font-medium text-n-slate-12 truncate">
        {{ label }}
      </span>
      <span
        class="i-lucide-chevron-right size-3.5 text-n-slate-10 flex-shrink-0 opacity-70 group-hover:opacity-100"
        aria-hidden="true"
      />
    </RouterLink>
  </div>
  <div v-else class="px-1 pb-1">
    <RouterLink
      :to="to"
      class="flex items-center justify-center size-8 rounded-lg border border-n-weak bg-n-solid-2 outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-safe:transition-colors hover:bg-n-alpha-2"
      :title="label"
      :aria-label="label"
    >
      <span
        class="size-2 rounded-full"
        :class="isPro ? 'bg-n-brand' : 'bg-n-slate-9'"
      />
    </RouterLink>
  </div>
</template>
